import haversine from "haversine";
import type {CampusLocation} from "@repo/actions/AcademyApi/GetCampuses";
import {getCloudflareGeoLocation} from "./serverUtils";

const GEOLOCATION_OPTIONS: PositionOptions = {
	enableHighAccuracy: false,
	timeout: 10_000,
	maximumAge: 5 * 60 * 1000,
};

type GeoLocation = {
	latitude: number;
	longitude: number;
};

export type BrowserGeoLocationResult = {
	location: GeoLocation | null;
	/** True when the user (or browser policy) denied geolocation access. */
	permissionDenied: boolean;
};

/**
 * A campus plus optional distance from the current search / location origin.
 * `distanceMiles` is null when no origin is set or the campus lacks coordinates.
 */
export type CampusWithDistance = {
	campus: CampusLocation;
	distanceMiles: number | null;
};

/** Max distance (miles) for campuses shown after a location search. */
export const CAMPUS_SEARCH_MAX_DISTANCE_MILES = 25;

const SEARCH_AREA_ROUNDING_FACTOR = 10;

/**
 * A coordinate coarsened for analytics to one decimal degree — a ~7-mile cell,
 * too blunt to stand in for the address behind it but still mappable.
 */
export function toSearchArea(coordinate: number): number {
	return (
		Math.round(coordinate * SEARCH_AREA_ROUNDING_FACTOR) /
		SEARCH_AREA_ROUNDING_FACTOR
	);
}

/**
 * Miles from the origin to a campus, or null when the campus has no coordinates.
 */
export function distanceMilesToCampus(
	campus: CampusLocation,
	latitude: number,
	longitude: number
): number | null {
	if (campus.latitude == null || campus.longitude == null) {
		return null;
	}

	return haversine(
		{latitude, longitude},
		{latitude: campus.latitude, longitude: campus.longitude},
		{unit: "mile"}
	);
}

/**
 * Campuses ordered nearest-first, each paired with distance in miles.
 * Entries without coordinates are pushed to the end (stable among themselves)
 * and have `distanceMiles: null`, unless `maxDistanceMiles` is set — then only
 * campuses within that radius (with known coordinates) are included.
 */
export function sortCampusesByDistance(
	campuses: CampusLocation[],
	latitude: number,
	longitude: number,
	maxDistanceMiles?: number
): CampusWithDistance[] {
	return campuses
		.map((campus, index) => ({
			campus,
			index,
			distanceMiles: distanceMilesToCampus(campus, latitude, longitude),
		}))
		.filter(({distanceMiles}) => {
			if (maxDistanceMiles == null) {
				return true;
			}
			return distanceMiles != null && distanceMiles <= maxDistanceMiles;
		})
		.sort((a, b) => {
			if (a.distanceMiles == null && b.distanceMiles == null) {
				return a.index - b.index;
			}
			if (a.distanceMiles == null) {
				return 1;
			}
			if (b.distanceMiles == null) {
				return -1;
			}
			if (a.distanceMiles !== b.distanceMiles) {
				return a.distanceMiles - b.distanceMiles;
			}
			return a.index - b.index;
		})
		.map(({campus, distanceMiles}) => ({campus, distanceMiles}));
}

/**
 * Campuses whose address state matches the two-letter abbreviation (case-insensitive).
 */
export function filterCampusesByState(
	campuses: CampusLocation[],
	stateCode: string
): CampusLocation[] {
	const normalizedState = stateCode.trim().toUpperCase();
	if (!normalizedState) {
		return [];
	}

	return campuses.filter(
		(campus) => campus.address?.state.trim().toUpperCase() === normalizedState
	);
}

/**
 * Search results for a geocoded origin: state searches match by address.state
 * (no mile radius); city/ZIP searches keep the 25-mile radius.
 */
export function getCampusesForSearchOrigin(
	campuses: CampusLocation[],
	latitude: number,
	longitude: number,
	stateCode: string | null
): CampusWithDistance[] {
	if (stateCode) {
		return sortCampusesByDistance(
			filterCampusesByState(campuses, stateCode),
			latitude,
			longitude
		);
	}

	return sortCampusesByDistance(
		campuses,
		latitude,
		longitude,
		CAMPUS_SEARCH_MAX_DISTANCE_MILES
	);
}

/**
 * Returns the campus nearest to the given coordinates, or null if none have
 * usable latitude/longitude.
 */
export function findClosestCampus(
	campuses: CampusLocation[],
	latitude: number,
	longitude: number
): CampusLocation | null {
	const closest = sortCampusesByDistance(campuses, latitude, longitude)[0];
	if (
		!closest ||
		closest.campus.latitude == null ||
		closest.campus.longitude == null
	) {
		return null;
	}
	return closest.campus;
}

/**
 * Gets the browser location from the browser geolocation API.
 * Returns null location when geolocation is unavailable, denied, or fails.
 *
 * Call this synchronously inside a click handler so the browser can show
 * the location permission prompt (user activation must not be lost).
 */
export function requestBrowserGeoLocation(): Promise<BrowserGeoLocationResult> {
	if (typeof navigator === "undefined" || !navigator.geolocation) {
		return Promise.resolve({location: null, permissionDenied: false});
	}

	return new Promise((resolve) => {
		navigator.geolocation.getCurrentPosition(
			(position) => {
				resolve({
					location: {
						latitude: position.coords.latitude,
						longitude: position.coords.longitude,
					},
					permissionDenied: false,
				});
			},
			(error) => {
				resolve({
					location: null,
					permissionDenied: error.code === error.PERMISSION_DENIED,
				});
			},
			GEOLOCATION_OPTIONS
		);
	});
}

/**
 * True when this origin’s geolocation permission is already denied.
 * Returns false when the Permissions API cannot report a status.
 */
export async function isBrowserGeolocationDenied(): Promise<boolean> {
	if (typeof navigator === "undefined" || !navigator.permissions?.query) {
		return false;
	}

	try {
		const status = await navigator.permissions.query({name: "geolocation"});
		return status.state === "denied";
	} catch {
		return false;
	}
}

/**
 * Resolves visitor coordinates from browser geolocation, falling back to
 * Cloudflare IP geolocation headers when browser geolocation is unavailable
 * or denied. Returns null when no location is available.
 */
export async function requestUserGeoLocation(): Promise<GeoLocation | null> {
	const {location} = await requestBrowserGeoLocation();
	return location ?? (await getCloudflareGeoLocation());
}

/**
 * Resolves the nearest campus from browser geolocation, falling back to
 * Cloudflare IP geolocation headers when browser geolocation is unavailable
 * or denied. Returns null when no location or matching campus is found.
 */
export async function requestClosestCampusFromGeolocation(
	campuses: CampusLocation[]
): Promise<CampusLocation | null> {
	if (!campuses.length) {
		return null;
	}

	const location = await requestUserGeoLocation();
	if (!location) {
		return null;
	}

	return findClosestCampus(campuses, location.latitude, location.longitude);
}
